import { useState } from 'react';
import { useGameStore } from '../store';
import { RARITY } from '../data/cards';

// Rarity color mapping
const rarityColors = {
  [RARITY.COMMON]: 'text-gray-400 border-gray-400',
  [RARITY.RARE]: 'text-blue-400 border-blue-400',
  [RARITY.EPIC]: 'text-purple-400 border-purple-400',
  [RARITY.LEGENDARY]: 'text-yellow-400 border-yellow-400'
};

const rarityBgColors = {
  [RARITY.COMMON]: 'bg-gray-700',
  [RARITY.RARE]: 'bg-blue-700',
  [RARITY.EPIC]: 'bg-purple-700',
  [RARITY.LEGENDARY]: 'bg-yellow-600'
};

// Progress bar colors based on rarity
const progressBarColors = {
  [RARITY.COMMON]: 'bg-gray-400',
  [RARITY.RARE]: 'bg-blue-400',
  [RARITY.EPIC]: 'bg-purple-400',
  [RARITY.LEGENDARY]: 'bg-yellow-400'
};

function CardItem({ card, onActivate, onDeactivate, onDiscard, onComplete, isActive, daysRemaining, progress }) {
  const baseColorClass = rarityColors[card.rarity] || 'text-gray-400 border-gray-400';

  const hasTrackableProgress = card.requirement?.type !== 'none' && progress?.target > 0;
  const isComplete = hasTrackableProgress && progress.current >= progress.target;

  // Styling priority: complete > expiring soon > normal
  let borderClass = baseColorClass;
  let pulseClass = '';
  let glowClass = '';

  if (isComplete) {
    // Completable card - green highlight with pulse
    borderClass = 'border-green-400 border-2';
    pulseClass = 'animate-pulse';
    glowClass = 'shadow-lg shadow-green-500/50';
  } else if (daysRemaining !== null) {
    // Expiration warning styling
    if (daysRemaining <= 1) {
      borderClass = 'text-red-400 border-red-500 border-2';
      pulseClass = 'animate-pulse';
    } else if (daysRemaining <= 2) {
      borderClass = 'text-orange-400 border-orange-500 border-2';
    }
  }
  const progressBarColor = progressBarColors[card.rarity] || 'bg-gray-400';

  const handleDiscard = () => {
    const confirmed = window.confirm(`Discard "${card.name}"? This cannot be undone.`);
    if (confirmed) {
      onDiscard(card.id);
    }
  };

  return (
    <div className={`border ${borderClass} ${pulseClass} ${glowClass} p-2 mb-2 rounded`}>
      {/* Card Header */}
      <div className="flex justify-between items-center gap-2 mb-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-sm text-white truncate">{card.name}</span>
          <span className={`text-xs uppercase flex-shrink-0 ${rarityColors[card.rarity]}`}>
            <span className="hidden sm:inline">({card.rarity})</span>
            <span className="sm:hidden">({card.rarity.charAt(0)})</span>
          </span>
        </div>
        {/* Ready indicator - always visible, outside truncating container */}
        {isComplete && (
          <span className="text-xs px-2 py-0.5 bg-green-500 text-white rounded font-bold flex-shrink-0">
            Ready!
          </span>
        )}
        {/* Expiration Display */}
        {daysRemaining !== null && !isComplete && (
          <span className={`text-xs flex-shrink-0 whitespace-nowrap ${daysRemaining <= 1 ? 'text-red-400 font-bold' : daysRemaining <= 2 ? 'text-orange-400' : 'text-gray-400'}`}>
            {daysRemaining <= 0 ? 'Expired!' : `${daysRemaining}d left`}
          </span>
        )}
      </div>

      {/* Card Description */}
      <p className="text-xs text-gray-400 mb-2">{card.description}</p>

      {/* Progress Bar (only for trackable cards) */}
      {hasTrackableProgress && (
        <div className="mb-2">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>{progress.current}/{progress.target}</span>
            <span>{progress.percentage}%</span>
          </div>
          <div className="w-full bg-gray-700 rounded h-2">
            <div
              className={`${progressBarColor} h-2 rounded transition-all duration-300`}
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Reward Display */}
      <div className="text-xs text-teal mb-2">
        Reward: +{card.reward} M
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        {isComplete && (
          <button
            onClick={() => onComplete(card.id)}
            className="text-xs px-4 py-2 sm:py-1.5 w-full sm:w-auto bg-green-500 text-white rounded hover:bg-green-400 font-bold shadow-lg shadow-green-500/50 animate-pulse"
          >
            Claim +{card.reward} M
          </button>
        )}
        {isActive ? (
          <button
            onClick={() => onDeactivate(card.id)}
            className="text-xs px-2 py-2 sm:py-1 w-full sm:w-auto bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
          >
            Deactivate
          </button>
        ) : (
          <>
            <button
              onClick={() => onActivate(card.id)}
              className="text-xs px-2 py-2 sm:py-1 w-full sm:w-auto bg-teal text-dark rounded hover:opacity-80"
            >
              Activate
            </button>
            <button
              onClick={handleDiscard}
              className="text-xs px-2 py-2 sm:py-1 w-full sm:w-auto bg-red-600 text-white rounded hover:bg-red-500"
            >
              Discard
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function DrawResult({ result, onClose }) {
  if (!result) return null;

  const bgColor = result.success
    ? rarityBgColors[result.card.rarity]
    : 'bg-gray-700';

  return (
    <div className={`${bgColor} p-3 rounded mb-4 text-center`}>
      {result.success ? (
        <>
          <p className="text-xs text-gray-300 mb-1">You drew:</p>
          <p className="text-lg font-bold text-white">{result.card.name}</p>
          <p className="text-xs uppercase text-gray-300">({result.card.rarity})</p>
        </>
      ) : (
        <p className="text-sm text-gray-300">
          {result.reason === 'already_drawn'
            ? 'Already claimed today!'
            : 'Vault is full!'}
        </p>
      )}
      <button
        onClick={onClose}
        className="mt-2 text-xs text-gray-400 hover:text-white"
      >
        Dismiss
      </button>
    </div>
  );
}

function CompletionResult({ result, onClose }) {
  if (!result) return null;

  return (
    <div className="bg-green-700 p-3 rounded mb-4 text-center">
      <p className="text-xs text-green-200 mb-1">Card Completed!</p>
      <p className="text-lg font-bold text-white">{result.cardName}</p>
      {result.doublerUsed ? (
        <>
          <p className="text-sm text-yellow-300 font-bold">The Doubler activated!</p>
          <p className="text-sm text-green-200">
            +{result.reward} M (doubled!) + {result.doublerReward} M (Doubler bonus)
          </p>
          <p className="text-sm text-green-100 font-bold">
            Total: +{result.totalReward} M
          </p>
        </>
      ) : (
        <p className="text-sm text-green-200">+{result.reward} M earned!</p>
      )}
      <button
        onClick={onClose}
        className="mt-2 text-xs text-green-300 hover:text-white"
      >
        Dismiss
      </button>
    </div>
  );
}

export function CardVault() {
  const [drawResult, setDrawResult] = useState(null);
  const [completionResult, setCompletionResult] = useState(null);

  const cards = useGameStore((state) => state.cards);
  const lastDrawDate = useGameStore((state) => state.lastDrawDate);
  const gameDate = useGameStore((state) => state.gameDate);
  const getActiveCards = useGameStore((state) => state.getActiveCards);
  const getVaultCards = useGameStore((state) => state.getVaultCards);
  const activateCard = useGameStore((state) => state.activateCard);
  const deactivateCard = useGameStore((state) => state.deactivateCard);
  const removeCard = useGameStore((state) => state.removeCard);
  const claimDailyDraw = useGameStore((state) => state.claimDailyDraw);
  const initializeStarterCards = useGameStore((state) => state.initializeStarterCards);
  const getCardTimeRemaining = useGameStore((state) => state.getCardTimeRemaining);
  const getCardProgress = useGameStore((state) => state.getCardProgress);
  const completeCard = useGameStore((state) => state.completeCard);

  const activeCards = getActiveCards();
  const vaultCards = getVaultCards();
  const canDraw = lastDrawDate !== gameDate;
  const isNewUser = cards.length === 0;

  const handleDailyDraw = () => {
    const result = claimDailyDraw();
    setDrawResult(result);
  };

  const handleInitialize = () => {
    const result = initializeStarterCards();
    if (result.initialized) {
      setDrawResult({
        success: true,
        card: { name: '3 Starter Cards', rarity: RARITY.COMMON }
      });
    }
  };

  const handleComplete = (cardId) => {
    const result = completeCard(cardId);
    if (result.success) {
      setCompletionResult(result);
    }
  };

  return (
    <div className="container-game mt-6">
      {/* Draw Result */}
      <DrawResult result={drawResult} onClose={() => setDrawResult(null)} />

      {/* Completion Result */}
      <CompletionResult result={completionResult} onClose={() => setCompletionResult(null)} />

      {/* Draw Controls */}
      <div className="mb-4 sm:mb-6 flex gap-2">
        {isNewUser ? (
          <button
            onClick={handleInitialize}
            className="flex-1 py-2 bg-teal text-dark rounded hover:opacity-80 text-sm font-bold"
          >
            Get Starter Cards
          </button>
        ) : (
          <button
            onClick={handleDailyDraw}
            disabled={!canDraw}
            className={`flex-1 py-2 rounded text-sm font-bold ${
              canDraw
                ? 'bg-teal text-dark hover:opacity-80'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            {canDraw ? 'Daily Draw' : 'Drawn Today'}
          </button>
        )}
      </div>

      {/* Last Draw Info */}
      {lastDrawDate && (
        <p className="text-xs text-gray-500 text-center mb-4">
          Last draw: {lastDrawDate}
        </p>
      )}

      {/* Active Cards Section */}
      <div className="mb-4 sm:mb-6">
        <h2 className="font-pixel text-teal text-sm mb-2 sm:mb-3">
          Active Cards ({activeCards.length}/5)
        </h2>
        <div className="bg-gray-900 p-2 sm:p-3 rounded min-h-[80px]">
          {activeCards.length === 0 ? (
            <p className="text-gray-500 text-xs text-center">No active cards</p>
          ) : (
            activeCards.map((card) => (
              <CardItem
                key={card.id}
                card={card}
                isActive={true}
                onDeactivate={deactivateCard}
                onComplete={handleComplete}
                daysRemaining={getCardTimeRemaining(card.id)}
                progress={getCardProgress(card.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Vault Section */}
      <div className="mb-4 sm:mb-6">
        <h2 className="font-pixel text-teal text-sm mb-2 sm:mb-3">
          Vault ({vaultCards.length}/15)
        </h2>
        <div className="bg-gray-900 p-2 sm:p-3 rounded min-h-[80px]">
          {vaultCards.length === 0 ? (
            <p className="text-gray-500 text-xs text-center">Vault is empty</p>
          ) : (
            vaultCards.map((card) => (
              <CardItem
                key={card.id}
                card={card}
                isActive={false}
                onActivate={activateCard}
                onDiscard={removeCard}
                onComplete={handleComplete}
                daysRemaining={getCardTimeRemaining(card.id)}
                progress={getCardProgress(card.id)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
